// Shamil spec portal — admin-only page where Shamil (and Beshan) work through
// the 17 domain questions. Cards grouped by category, per-card status pill,
// answer textarea, multi-file upload.

import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  specApi,
  SPEC_CATEGORY_LABELS,
  SPEC_STATUS_TONE,
  type SpecCategory,
  type SpecItem,
  type SpecStatus,
} from "../lib/spec";

export function SpecPage() {
  const { data, isLoading } = useQuery({ queryKey: ["spec"], queryFn: () => specApi.list() });

  const grouped = useMemo(() => {
    const out: Record<SpecCategory, SpecItem[]> = {
      file_formats: [],
      worked_examples: [],
      judgement: [],
      validation: [],
      fixtures: [],
    };
    for (const i of data?.items ?? []) out[i.category].push(i);
    return out;
  }, [data]);

  const counts = useMemo(() => {
    const items = data?.items ?? [];
    return {
      total: items.length,
      pending: items.filter((i) => i.status === "pending").length,
      in_progress: items.filter((i) => i.status === "in_progress").length,
      answered: items.filter((i) => i.status === "answered").length,
      blocked: items.filter((i) => i.status === "blocked").length,
    };
  }, [data]);

  const order: SpecCategory[] = [
    "file_formats",
    "worked_examples",
    "judgement",
    "validation",
    "fixtures",
  ];

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Spec portal · Keytiqa</div>
          <h1 className="stage-title">
            Domain questions for <em>Shamil.</em>
          </h1>
          <p className="stage-sub">
            Seventeen items unblock weeks 5–7 (tax engine, payroll, RAMIS export). Answer inline,
            mark status, attach files. Beshan and Shamil both see the same page; both can edit.
          </p>
        </div>
        <div className="stage-meta">
          <span>{counts.answered}/{counts.total} answered</span>
          {counts.blocked > 0 && (
            <span style={{ color: "var(--rose)" }}>· {counts.blocked} blocked</span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : (
        <>
          <ProgressBar counts={counts} />
          {order.map((cat) =>
            grouped[cat].length === 0 ? null : (
              <CategorySection key={cat} category={cat} items={grouped[cat]} />
            )
          )}
        </>
      )}
    </>
  );
}

function ProgressBar({
  counts,
}: {
  counts: { total: number; pending: number; in_progress: number; answered: number; blocked: number };
}) {
  const segs = [
    { k: "answered" as const, n: counts.answered, color: "var(--accent)" },
    { k: "in_progress" as const, n: counts.in_progress, color: "var(--gold)" },
    { k: "blocked" as const, n: counts.blocked, color: "var(--rose)" },
    { k: "pending" as const, n: counts.pending, color: "var(--line-2)" },
  ];
  return (
    <div className="card card-pad-lg" style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", marginBottom: 10, height: 14, borderRadius: 100, overflow: "hidden", background: "var(--paper-2)" }}>
        {segs.map((s) =>
          s.n === 0 ? null : (
            <div
              key={s.k}
              title={`${s.k}: ${s.n}`}
              style={{ flex: s.n, background: s.color, transition: "flex 300ms" }}
            />
          )
        )}
      </div>
      <div style={{ display: "flex", gap: 18, fontSize: 12, color: "var(--ink-3)" }}>
        {segs.map((s) => (
          <span key={s.k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 9,
                height: 9,
                borderRadius: 2,
                background: s.color,
              }}
            />
            {SPEC_STATUS_TONE[s.k].label} · {s.n}
          </span>
        ))}
      </div>
    </div>
  );
}

function CategorySection({ category, items }: { category: SpecCategory; items: SpecItem[] }) {
  const meta = SPEC_CATEGORY_LABELS[category];
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ marginBottom: 14 }}>
        <div className="stage-eyebrow" style={{ color: "var(--ink-3)" }}>
          {category.replace("_", " ")}
        </div>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 500 }}>
          {meta.label}
        </div>
        <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{meta.sub}</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((item) => (
          <ItemCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function ItemCard({ item }: { item: SpecItem }) {
  const tone = SPEC_STATUS_TONE[item.status];
  const [draft, setDraft] = useState(item.answer_text ?? "");
  const [status, setStatus] = useState<SpecStatus>(item.status);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const qc = useQueryClient();
  useEffect(() => {
    setDraft(item.answer_text ?? "");
    setStatus(item.status);
  }, [item.id, item.answer_text, item.status]);

  async function save() {
    setSaving(true);
    try {
      await specApi.patch(item.key, { status, answer_text: draft });
      setSavedAt(new Date().toISOString());
      qc.invalidateQueries({ queryKey: ["spec"] });
    } finally {
      setSaving(false);
    }
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const fs = e.target.files;
    if (!fs || fs.length === 0) return;
    setUploadErr(null);
    try {
      await specApi.uploadFiles(item.key, Array.from(fs));
      qc.invalidateQueries({ queryKey: ["spec"] });
    } catch (err) {
      setUploadErr((err as Error).message);
    } finally {
      e.target.value = "";
    }
  }

  return (
    <div className="card card-pad-lg">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: "var(--ink-4)",
              }}
            >
              #{String(item.position).padStart(2, "0")}
            </span>
            <span
              style={{
                padding: "3px 10px",
                borderRadius: 100,
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                background: tone.bg,
                color: tone.fg,
              }}
            >
              {tone.label}
            </span>
            {item.updated_by && (
              <span style={{ fontSize: 11, color: "var(--ink-4)" }}>
                · last by {item.updated_by.name}
                {item.updated_at && ` · ${new Date(item.updated_at).toLocaleDateString("en-LK", { day: "2-digit", month: "short" })}`}
              </span>
            )}
          </div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 17, fontWeight: 500, marginBottom: 4 }}>
            {item.question}
          </div>
          {item.context && (
            <div style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: 1.55 }}>
              {item.context}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        <textarea
          className="form-input"
          rows={3}
          placeholder="Answer · paste IRD reference · explain the rule…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{ fontFamily: "inherit", resize: "vertical" }}
        />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label className="form-label" style={{ marginBottom: 0 }}>
              Status
            </label>
            <select
              className="form-input"
              style={{ width: "auto", padding: "6px 10px", fontSize: 13 }}
              value={status}
              onChange={(e) => setStatus(e.target.value as SpecStatus)}
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="answered">Answered</option>
              <option value="blocked">Blocked</option>
            </select>
            <button className="btn btn-primary" style={{ padding: "8px 16px", fontSize: 13 }} onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            {savedAt && (
              <span style={{ fontSize: 11, color: "var(--accent-deep)" }}>
                Saved ✓
              </span>
            )}
          </div>

          <label
            className="btn btn-line"
            style={{ padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
          >
            + Attach files
            <input type="file" hidden multiple onChange={onPick} />
          </label>
        </div>

        {uploadErr && <div className="banner banner-error">{uploadErr}</div>}

        {item.files.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
            {item.files.map((f) => (
              <div
                key={f.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  background: "var(--paper-2)",
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                <a
                  href={specApi.fileUrl(f.id)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 500 }}
                >
                  {f.filename}
                </a>
                <span style={{ fontSize: 11, color: "var(--ink-4)" }}>
                  {(f.byte_size / 1024).toFixed(1)} KB · {new Date(f.uploaded_at).toLocaleDateString("en-LK", { day: "2-digit", month: "short" })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
